import BaseSection from '@/sections/base'
import type { VariantChangeEvent } from '@/components/product/variantPicker'
import ProductDetailForm from '@/components/product/productDetailForm'
import ProductDetailGallery from '@/components/product/productDetailGallery'
import ProductThumbnails from '@/components/product/productThumbnails'
import Drawer from '@/components/drawer'
import Accordion from '@/components/accordion'
import Lightbox from '@/components/lightbox'

// Reference drawers triggered from the details row under the Add-to-Bag button.
const DRAWER_IDS = ['SizeGuideDrawer', 'FabricDrawer', 'CareDrawer']

export default class ProductSection extends BaseSection {
  static TYPE = 'product'

  productDetailForm: ProductDetailForm
  galleries: ProductDetailGallery[]
  thumbnails: ProductThumbnails | null
  drawers: Drawer[]
  accordions: Accordion[]
  lightbox: Lightbox

  constructor(container: HTMLElement) {
    super(container)

    this.productDetailForm = new ProductDetailForm(this.qs(ProductDetailForm.SELECTOR) as HTMLElement, {
      onVariantChange: this.onVariantChange.bind(this)
    })

    this.galleries = this.qsa(ProductDetailGallery.SELECTOR).map(el => {
      return new ProductDetailGallery(el)
    })

    const thumbsEl = this.qs(ProductThumbnails.SELECTOR)
    this.thumbnails = thumbsEl ? new ProductThumbnails(thumbsEl as HTMLElement) : null

    // The registry red numbers render statically (no decode/scramble on load).
    // The RegistryFrame scramble component (_scripts/components/registryFrame.ts)
    // is kept for reuse elsewhere.

    // Move each reference drawer OUT of the taxi view (into <body>) so its
    // position:fixed is relative to the viewport — not the taxi view, which is
    // transformed during the enter transition and would push the off-screen drawer
    // past the right edge (horizontal-scrollbar flash on load) and break the
    // backdrop-filter blur. Same reason the cart drawer lives outside the taxi view.
    this.drawers = DRAWER_IDS
      .map(id => this.qs(`#${id}`))
      .filter((el): el is HTMLElement => Boolean(el))
      .map(el => {
        document.body.appendChild(el)
        return new Drawer(el)
      })

    this.accordions = this.qsa(Accordion.SELECTOR).map(el => {
      return new Accordion(el)
    })

    this.lightbox = new Lightbox(this.qs(Lightbox.SELECTOR) as HTMLElement)

    // Filter the gallery to the initially selected colour on load, so a
    // multi-colour product doesn't show every colour's images mixed together.
    this.applyInitialColorFilter()
  }

  get colorOptionIdx() {
    return this.productDetailForm.product.options_with_values.findIndex(
      opt => opt.name.toLowerCase() === 'color'
    )
  }

  applyInitialColorFilter() {
    const gallery = this.galleries[0]
    if (!gallery) return

    const selectedColor = this.colorOptionIdx !== -1
      ? this.productDetailForm.product.options_with_values[this.colorOptionIdx]?.selected_value
      : ''

    gallery.filterByColor(selectedColor ?? '')
    this.thumbnails?.filterByColor(selectedColor ?? '')
  }

  onVariantChange(e: VariantChangeEvent) {
    const { selectedOptions } = e
    const gallery = this.galleries[0]
    if (!gallery) return

    const selectedColor = this.colorOptionIdx !== -1 ? selectedOptions[this.colorOptionIdx]?.value : ''

    gallery.filterByColor(selectedColor ?? '')
    this.thumbnails?.filterByColor(selectedColor ?? '')
  }

  onUnload() {
    super.onUnload()

    // The drawers were reparented to <body>, so they (and their backdrops) aren't
    // torn down with the section DOM — remove them here to avoid orphans on navigation.
    DRAWER_IDS.forEach(id => {
      document.getElementById(id)?.remove()
      document.querySelector(`.backdrop[aria-controls="${id}"]`)?.remove()
    })
  }
}